import React, { useCallback, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import AreaTitle from '../../../components/areaTitle/AreaTitle';
import Toast from '../../../components/support/Toast';
import SequenceSwitch from './components/SequenceSwitch';
import SimulationPanel from './components/SimulationPanel';
import TemplatePreviewDialog from './components/TemplatePreviewDialog';
import TemplatesTable from './components/TemplatesTable';
import TemplateEditorDialog from './components/editor/TemplateEditorDialog';
import useOnboardingTemplates from './hooks/useOnboardingTemplates';
import { groupTemplatesByStage } from './onboardingAdminConfig';

/**
 * AdminOnboarding - E-mails de acompanhamento (Admin Geral)
 *
 * Sequência de e-mails enviada às contas em teste (docs/features/onboarding/README.md):
 * chave geral, modelos por etapa da jornada (com código da variante e números), prévia direto da lista, editor com prévia e simulação.
 * Nada aqui depende do usuário ou da empresa do admin logado.
 */
function AdminOnboarding() {
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);

  const notify = useCallback((message, severity = 'success') => {
    setToast({ open: true, message, severity });
  }, []);

  const { data, loading, error, setSequenceEnabled, isSavingSequence, saveTemplate } = useOnboardingTemplates(notify);

  const lists = useMemo(() => ({
    targets: data?.targets || [],
    variables: data?.variables || [],
    blocks: data?.blocks || [],
  }), [data]);

  const templates = useMemo(() => data?.templates || [], [data]);
  const groups = useMemo(() => groupTemplatesByStage(templates, data?.stages), [templates, data]);
  const enabledCount = templates.filter((item) => item.enabled).length;

  return (
    <Box sx={{ backgroundColor: '#F8F9FC', minHeight: '100vh', p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <AreaTitle
          Title="Admin Geral · Acompanhamento"
          Icon={<MarkEmailReadOutlinedIcon />}
          Description="E-mails que ajudam quem está no teste a dar os primeiros passos."
        />
      </Box>

      {loading && !data && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {data && (
        <>
          <SequenceSwitch
            settings={data.settings}
            enabledCount={enabledCount}
            saving={isSavingSequence}
            onChange={setSequenceEnabled}
          />

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Cliques mostram se o e-mail ajudou. Aberturas são aproximadas: o iPhone abre as imagens sozinho e infla esse número.
          </Typography>

          {groups.map((group) => (
            <TemplatesTable
              key={group.key}
              group={group}
              targets={lists.targets}
              onEdit={setEditing}
              onPreview={setPreviewing}
            />
          ))}

          <SimulationPanel />
        </>
      )}

      {previewing && (
        <TemplatePreviewDialog
          key={previewing.key}
          template={previewing}
          onClose={() => setPreviewing(null)}
          onEdit={(template) => {
            setPreviewing(null);
            setEditing(template);
          }}
          notify={notify}
        />
      )}

      <TemplateEditorDialog
        template={editing}
        lists={lists}
        onClose={() => setEditing(null)}
        onSave={saveTemplate}
        notify={notify}
      />

      <Toast
        open={toast.open}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        message={toast.message}
        severity={toast.severity}
        autoHideDuration={6000}
      />
    </Box>
  );
}

export default AdminOnboarding;
